import { scanText } from "@/lib/pii-engine";
import { buildContactUsActionMetadata, type ContactUsActionMetadata } from "@/lib/chat-fast-paths";

export type PrivacyGreetingVariant = "full" | "compact";

export interface SensitiveDataGuardContext {
  agentName?: string | null;
  systemPrompt?: string | null;
  complianceMode?: string | null;
  vertical?: string | null;
  contactUsHref?: string | null;
}

export type SensitiveDataType =
  | "nhs_number"
  | "national_insurance"
  | "payment_card"
  | "credential"
  | "address_private_detail"
  | "patient_confidential"
  | "phone_number"
  | "driving_licence"
  | "identity_document";

export interface SensitiveDataGuardReply {
  sensitiveDataType: SensitiveDataType;
  reply: string;
  metadata?: ContactUsActionMetadata;
}

// Short, production-friendly privacy warning. Detailed sensitive-data
// replies (per type) still appear when the user actually shares
// regulated data — see replyForSensitiveType below.
export const PRIVACY_GREETING =
  "I’m an AI assistant, not a human. Please don’t share confidential, medical, payment, identity, or other sensitive details here. How can I help?";

const FULL_PRIVACY_GREETING = PRIVACY_GREETING;

const COMPACT_PRIVACY_GREETING = PRIVACY_GREETING;

const CREDENTIAL_PATTERNS: RegExp[] = [
  /\bpassword\b/i,
  /\bapi[-\s]?key\b/i,
  /\baccess token\b/i,
  /\brefresh token\b/i,
  /\bbearer token\b/i,
  /\bsecret\b/i,
  /\bprivate key\b/i,
  /\bjwt\b/i,
];

const PATIENT_CONFIDENTIAL_PATTERNS: RegExp[] = [
  /\bpatient(?:'s)?\s+(?:information|details|records?|history|notes?|symptoms?|treatment|prescription|confidential|medication|medicine|antibiotic(?:s)?|appointment details?)\b/i,
  /\b(?:patient|the patient)\s+(?:address|home address|residential address|appointment details?|identity details?|medical details?|clinical details?)\b/i,
  /\b(?:medical|clinical|health)\s+(?:records?|notes?|details|history|symptoms?|treatment|prescription|medication|medicine|antibiotic(?:s)?|dose|dosage)\b/i,
  /\b(?:medication|medicine|antibiotic(?:s)?|prescription|symptom(?:s)?|dose|dosage)\b/i,
  /\bdetailed patient information\b/i,
  /\bpatient confidential\b/i,
  /\bprivate medical details?\b/i,
];

const ADDRESS_PRIVATE_PATTERNS: RegExp[] = [
  /\bmy (?:home |postal |residential )?address is\b/i,
  /\b(?:home|postal|residential) address\b/i,
  /\b\d{1,4}\s+[a-z0-9' .-]{2,}\b(?:street|st|road|rd|avenue|ave|lane|ln|drive|dr|close|cl|court|ct|way|place|pl|crescent|terrace|gardens|grove)\b/i,
  /\b[a-z]{1,2}\d[a-z\d]?\s?\d[a-z]{2}\b/i,
];

const PHONE_HINT_PATTERNS: RegExp[] = [
  /\bphone number\b/i,
  /\bmobile number\b/i,
  /\btelephone number\b/i,
  /\bcall me\b/i,
  /\bcontact me on\b/i,
  /\bremember(?: it)? and call me later\b/i,
  /\bplease call me\b/i,
];

const UK_PHONE_PATTERNS: RegExp[] = [
  /\b07(?:[\s().-]*\d){9}\b/i,
  /\b(?:\+44|0044)\s*(?:\(0\)\s*)?7(?:[\s().-]*\d){9}\b/i,
];

const HEALTHCARE_CONTEXT_PATTERNS: RegExp[] = [
  /\bdental\b/i,
  /\bdentist\b/i,
  /\bclinic\b/i,
  /\bmedical\b/i,
  /\bhealthcare\b/i,
  /\bpatient\b/i,
  /\bhospital\b/i,
  /\boral\b/i,
  /\btooth\b/i,
  /\bteeth\b/i,
  /\borthodont/i,
  /\bpharmacy\b/i,
  /\bgp\b/i,
];

const NATIONAL_INSURANCE_HINT_PATTERNS: RegExp[] = [
  /\bnational insurance(?: number)?\b/i,
  /\buk national insurance\b/i,
  /\bnino\b/i,
  /\bni\b\s*(?:number|no\.?|#|is|=|:)/i,
];

const NATIONAL_INSURANCE_STYLE_PATTERN =
  /\b(?!BG|GB|KN|NK|NT|TN|ZZ)[A-CEGHJ-PR-TW-Z][A-CEGHJ-NPR-TW-Z]\s?\d{2}\s?\d{2}\s?\d{2}\s?[A-D]\b/i;

const ID_DOCUMENT_PATTERNS: RegExp[] = [
  /\bdriving licen[cs]e\b/i,
  /\bpassport\b/i,
  /\bidentity document\b/i,
  /\bid card\b/i,
];

function normalizeMessage(message: string): string {
  return message.toLowerCase().replace(/\s+/g, " ").trim();
}

function formatWarningReply(intro: string, guidance: string): string {
  return `${intro} ${guidance}`.trim();
}

function hasLikelyUkPhoneNumber(message: string): boolean {
  const compactPhone = message.replace(/[^\d+]/g, "");

  if (
    /^(?:\+44|0044)?7\d{9}$/.test(compactPhone) ||
    /^07\d{9}$/.test(compactPhone)
  ) {
    return true;
  }

  return UK_PHONE_PATTERNS.some((pattern) => pattern.test(message));
}

function hasHealthcareContext(context: SensitiveDataGuardContext): boolean {
  const normalizedName = context.agentName?.trim().toLowerCase() ?? "";
  const normalizedPrompt = context.systemPrompt?.trim().toLowerCase() ?? "";
  const normalizedMode = context.complianceMode?.trim().toLowerCase() ?? "";
  const normalizedVertical = context.vertical?.trim().toLowerCase() ?? "";

  const normalized = [normalizedName, normalizedPrompt, normalizedMode, normalizedVertical]
    .filter((value): value is string => typeof value === "string" && value.trim().length > 0)
    .join(" ")
    .toLowerCase();

  if (!normalized) {
    return false;
  }

  if (/\bmedical\b/i.test(normalizedMode) || /\bdental\b/i.test(normalizedMode)) {
    return true;
  }

  if (/\bmedical\b/i.test(normalizedVertical) || /\bdental\b/i.test(normalizedVertical)) {
    return true;
  }

  if (normalizedName.includes("aditya labs")) {
    return false;
  }

  if (HEALTHCARE_CONTEXT_PATTERNS.some((pattern) => pattern.test(normalizedName))) {
    return true;
  }

  if (normalizedName.length === 0 && HEALTHCARE_CONTEXT_PATTERNS.some((pattern) => pattern.test(normalizedPrompt))) {
    return true;
  }

  return false;
}

function replyForSensitiveType(type: SensitiveDataType, healthcareContext: boolean): string {
  switch (type) {
    case "nhs_number":
      return healthcareContext
        ? formatWarningReply(
            "Please do not share NHS numbers in this chat.",
            "I cannot store or use NHS numbers here. For non-urgent follow-up, please use Contact Us."
          )
        : formatWarningReply(
            "Please do not share NHS numbers in this chat.",
            "I cannot store or use NHS numbers here. For non-urgent follow-up, please use Contact Us."
          );
    case "national_insurance":
      return healthcareContext
        ? formatWarningReply(
            "Please do not share National Insurance numbers in this chat.",
            "I cannot store or use them here. For non-urgent follow-up, please use Contact Us."
          )
        : formatWarningReply(
            "Please do not share National Insurance numbers in this chat.",
            "I cannot store or use them here. For non-urgent follow-up, please use Contact Us."
          );
    case "payment_card":
      return formatWarningReply(
        "Please do not share payment-card details in this chat.",
        "Payment details should only be entered through an approved secure payment page. For non-urgent support, please use Contact Us."
      );
    case "credential":
      return formatWarningReply(
        "Please do not share passwords, API keys, tokens, or secrets in this chat.",
        "I cannot store or manage credentials. If this was a real credential, please rotate it. For non-urgent support, please use Contact Us."
      );
    case "address_private_detail":
      return formatWarningReply(
        "Please do not share addresses or other private details in this chat.",
        "For non-urgent follow-up, please use Contact Us."
      );
    case "patient_confidential":
      return healthcareContext
        ? formatWarningReply(
            "Please do not share confidential patient details, addresses, or medical information in this chat.",
            "I can't diagnose symptoms, prescribe antibiotics, or replace a dentist. Please contact a qualified dental professional. If there is severe swelling, trouble breathing, trouble swallowing, or uncontrolled bleeding, use urgent care through the approved local process. For non-urgent follow-up, please use Contact Us."
          )
        : formatWarningReply(
            "Please do not share confidential patient details, addresses, medical information, or other sensitive details in this chat.",
            "I can’t provide clinical advice or handle private records here. For non-urgent follow-up, please use Contact Us."
          );
    case "phone_number":
      return formatWarningReply(
        "Please do not share phone numbers in this chat.",
        "For non-urgent follow-up, please use Contact Us."
      );
    case "driving_licence":
      return healthcareContext
        ? formatWarningReply(
            "Please do not share driving licence details in this chat.",
            "For identity details, please use Contact Us."
          )
        : formatWarningReply(
            "Please do not share driving licence details in this chat.",
            "For identity details, please use Contact Us."
          );
    case "identity_document":
      return healthcareContext
        ? formatWarningReply(
            "Please do not share identity documents in this chat.",
            "For identity details, please use Contact Us."
          )
        : formatWarningReply(
            "Please do not share identity documents in this chat.",
            "For identity details, please use Contact Us."
          );
    default:
      return FULL_PRIVACY_GREETING;
  }
}

function detectSensitiveDataType(message: string): SensitiveDataType | null {
  const normalized = normalizeMessage(message);

  if (CREDENTIAL_PATTERNS.some((pattern) => pattern.test(normalized))) {
    return "credential";
  }

  if (
    NATIONAL_INSURANCE_HINT_PATTERNS.some((pattern) => pattern.test(normalized)) ||
    NATIONAL_INSURANCE_STYLE_PATTERN.test(normalized)
  ) {
    return "national_insurance";
  }

  const scanMatches = scanText(message, [
    "nhs",
    "ni_number",
    "credit_card",
    "driving_license",
    "passport",
    "phone",
  ]);

  if (scanMatches.some((match) => match.key === "nhs")) {
    return "nhs_number";
  }

  if (scanMatches.some((match) => match.key === "credit_card")) {
    return "payment_card";
  }

  if (scanMatches.some((match) => match.key === "driving_license") || ID_DOCUMENT_PATTERNS[0].test(normalized)) {
    return "driving_licence";
  }

  if (scanMatches.some((match) => match.key === "passport") || ID_DOCUMENT_PATTERNS.slice(1).some((pattern) => pattern.test(normalized))) {
    return "identity_document";
  }

  if (hasLikelyUkPhoneNumber(message)) {
    return "phone_number";
  }

  if (PHONE_HINT_PATTERNS.some((pattern) => pattern.test(normalized))) {
    return "phone_number";
  }

  if (PATIENT_CONFIDENTIAL_PATTERNS.some((pattern) => pattern.test(normalized))) {
    return "patient_confidential";
  }

  if (ADDRESS_PRIVATE_PATTERNS.some((pattern) => pattern.test(normalized))) {
    return "address_private_detail";
  }

  if (scanMatches.some((match) => match.key === "phone")) {
    return "phone_number";
  }

  return null;
}

export function buildPrivacyGreeting(
  baseGreeting: string | undefined,
  variant: PrivacyGreetingVariant = "full"
): string {
  const trimmed = baseGreeting?.trim();
  const greeting = variant === "compact" ? COMPACT_PRIVACY_GREETING : FULL_PRIVACY_GREETING;

  if (!trimmed) {
    return greeting;
  }

  return greeting;
}

export function getSensitiveDataGuardReply(message: string): SensitiveDataGuardReply | null {
  return getSensitiveDataGuardReplyWithContext(message);
}

export function getSensitiveDataGuardReplyWithContext(
  message: string,
  context: SensitiveDataGuardContext = {}
): SensitiveDataGuardReply | null {
  const sensitiveDataType = detectSensitiveDataType(message);
  if (!sensitiveDataType) {
    return null;
  }

  const healthcareContext = hasHealthcareContext(context);

  return {
    sensitiveDataType,
    reply: replyForSensitiveType(sensitiveDataType, healthcareContext),
    metadata: buildContactUsActionMetadata(context.contactUsHref),
  };
}
